import { Brand, LanguageSelect, ThemeSwitch } from '@ly360/ui';
import {
  Briefcase,
  Building2,
  CalendarDays,
  CheckSquare,
  Handshake,
  Home,
  Inbox,
  LogOut,
  Megaphone,
  Menu,
  Newspaper,
  Workflow,
  Bot,
  MessageCircle,
  Package,
  PlugZap,
  ShoppingBag,
  TrendingUp,
  Target,
  Settings,
  ShieldCheck,
  TriangleAlert,
  UserRound,
  Users,
  UsersRound,
} from 'lucide-react';
import { useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, Outlet, useLocation } from 'react-router';
import { CommandBar } from '../assistant/CommandBar';
import { RealtimeProvider } from '../lib/realtime-context';
import { AvailabilityToggle } from '../pages/Calendar';
import { useAuth } from '../lib/auth';
import { packModules } from '@ly360/core';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number }>;
  perm?: string;
  soon?: boolean;
}

function NavItemLink({ item, onNavigate }: { item: NavItem; onNavigate: () => void }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  if (item.soon) {
    return (
      <span className="nav-link disabled" aria-disabled="true">
        <Icon size={17} />
        {item.label}
        <span className="soon">{t('nav.soon')}</span>
      </span>
    );
  }
  return (
    <NavLink
      to={item.to}
      className={({ isActive }) => `nav-link${isActive ? ' active' : ''}`}
      onClick={onNavigate}
    >
      <Icon size={17} />
      {item.label}
    </NavLink>
  );
}

export function Shell(): ReactNode {
  const { t, i18n } = useTranslation();
  const { session, logout, can, savePreferences } = useAuth();
  const location = useLocation();
  // Workspace pages (inbox, deals board) use the full width; reading pages keep a comfortable measure.
  const wide =
    /^\/(inbox|deals|growth|orders|products|campaigns|publishing|automations|admin\/assistant|admin\/whatsapp)(\/|$)/.test(
      location.pathname,
    );
  const [open, setOpen] = useState(false);
  if (!session) return null;

  const main: NavItem[] = [
    { to: '/home', label: t('nav.home'), icon: Home },
    { to: '/inbox', label: t('nav.inbox'), icon: Inbox, perm: 'conversations:read' },
    { to: '/people', label: t('nav.people'), icon: UserRound, perm: 'persons:read' },
    { to: '/companies', label: t('nav.companies'), icon: Building2, perm: 'accounts:read' },
    { to: '/deals', label: t('nav.deals'), icon: Handshake, perm: 'deals:read' },
    { to: '/cases', label: t('nav.cases'), icon: Briefcase, perm: 'cases:read' },
    { to: '/tasks', label: t('nav.tasks'), icon: CheckSquare },
    { to: '/calendar', label: t('nav.calendar'), icon: CalendarDays },
  ];
  // Industry pack modules (Retail: sales growth, orders, products, campaigns, audiences).
  const modules = packModules(session.tenant.industryPack);
  const sales: NavItem[] = [
    ...(modules.includes('growth')
      ? [{ to: '/growth', label: t('nav.growth'), icon: TrendingUp, perm: 'commerce:read' }]
      : []),
    ...(modules.includes('commerce')
      ? [
          { to: '/orders', label: t('nav.orders'), icon: ShoppingBag, perm: 'commerce:read' },
          { to: '/products', label: t('nav.products'), icon: Package, perm: 'commerce:read' },
        ]
      : []),
    ...(modules.includes('marketing')
      ? [
          { to: '/campaigns', label: t('nav.campaigns'), icon: Megaphone, perm: 'campaigns:read' },
          { to: '/publishing', label: t('nav.publishing'), icon: Newspaper, perm: 'campaigns:read' },
          { to: '/automations', label: t('nav.automations'), icon: Workflow, perm: 'campaigns:read' },
          { to: '/segments', label: t('nav.segments'), icon: Target, perm: 'campaigns:read' },
        ]
      : []),
  ];
  const admin: NavItem[] = [
    { to: '/admin/users', label: t('nav.users'), icon: Users, perm: 'users:read' },
    { to: '/admin/teams', label: t('nav.teams'), icon: UsersRound, perm: 'users:manage' },
    { to: '/admin/integrations', label: t('nav.integrations'), icon: PlugZap, perm: 'integrations:manage' },
    { to: '/admin/whatsapp', label: t('nav.whatsapp'), icon: MessageCircle, perm: 'integrations:manage' },
    { to: '/admin/assistant', label: t('nav.assistant'), icon: Bot, perm: 'assistant:manage' },
    { to: '/admin/audit', label: t('nav.audit'), icon: ShieldCheck, perm: 'audit:read' },
    { to: '/admin/failed-events', label: t('nav.deadLetters'), icon: TriangleAlert, perm: 'ops:dlq' },
  ];
  const visible = (items: NavItem[]) => items.filter((i) => !i.perm || can(i.perm));
  const ar = i18n.language === 'ar' || i18n.language === 'ur';
  const tenantName = ar && session.tenant.nameAr ? session.tenant.nameAr : session.tenant.name;
  const userName = ar && session.user.displayNameAr ? session.user.displayNameAr : session.user.displayName;
  const initials = userName
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('');
  const close = () => setOpen(false);

  return (
    <div className={`shell${open ? ' nav-open' : ''}`}>
      <a href="#main" className="skip-link">
        {t('common.skipToContent')}
      </a>
      <aside className="sidebar" aria-label={t('nav.menu')}>
        <Brand />
        <nav className="nav">
          <div className="nav-section">{t('nav.main')}</div>
          {visible(main).map((i) => (
            <NavItemLink key={i.to} item={i} onNavigate={close} />
          ))}
          {visible(sales).length > 0 && <div className="nav-section">{t('nav.salesMarketing')}</div>}
          {visible(sales).map((i) => (
            <NavItemLink key={i.to} item={i} onNavigate={close} />
          ))}
          {visible(admin).length > 0 && <div className="nav-section">{t('nav.admin')}</div>}
          {visible(admin).map((i) => (
            <NavItemLink key={i.to} item={i} onNavigate={close} />
          ))}
          <div className="nav-section" />
          <NavItemLink
            item={{ to: '/settings', label: t('nav.settings'), icon: Settings }}
            onNavigate={close}
          />
        </nav>
      </aside>
      {open && <div className="scrim" onClick={close} />}
      <div className="main">
        <header className="topbar">
          <button className="icon-btn menu-btn" onClick={() => setOpen(true)} aria-label={t('nav.menu')}>
            <Menu size={18} />
          </button>
          <div className="tenant">
            <strong data-testid="tenant-name" dir="auto">
              {tenantName}
            </strong>
          </div>
          <CommandBar />
          <div className="spacer" />
          {can('conversations:reply') && (
            <span className="hide-sm">
              <AvailabilityToggle compact />
            </span>
          )}
          <span className="hide-sm">
            <ThemeSwitch />
          </span>
          <span className="show-sm">
            <ThemeSwitch compact />
          </span>
          <span className="hide-sm">
            <LanguageSelect onChange={(l) => void savePreferences({ locale: l })} />
          </span>
          <span className="show-sm">
            <LanguageSelect compact onChange={(l) => void savePreferences({ locale: l })} />
          </span>
          <span className="avatar" title={userName} aria-hidden="true">
            {initials}
          </span>
          <button
            className="icon-btn"
            onClick={() => void logout()}
            aria-label={t('auth.signOut')}
            title={t('auth.signOut')}
          >
            <LogOut size={16} className="icon-dir" />
          </button>
        </header>
        <main id="main" className={`content${wide ? ' wide' : ''}`}>
          <RealtimeProvider token={session.accessToken}>
            <Outlet />
          </RealtimeProvider>
        </main>
      </div>
    </div>
  );
}
